// src/store/bidValidity/index.js
// SG2 — EPC Bid Validity Management
// Mirrors the supplier-portal Pinia store (SG1) at:
// c:\Users\RishikeshSingh\Desktop\SUPPLIER_PORTAL\src\stores\bidValidity.js
//
// Auth: Bearer token already injected globally via axios.defaults.headers.common
// (set on login in src/store/auth/index.js).
//
// API base URLs (env, already include /v1):
//   process.env.VUE_APP_API_BID   → e.g. https://r4s-bid-mgmt-drm-f5.dev.mintmesh.ai/v1
//   process.env.VUE_APP_REQ       → e.g. https://r4s-requisition-mgmt-drm-f5.dev.mintmesh.ai/v1
//
// State is keyed by bidId so multiple offers can be inspected without cache thrash.

import axios from 'axios'

// ---- helpers ----------------------------------------------------------------

function extractValidity(raw) {
  if (!raw) return null
  return {
    value_utc: raw.value_utc != null ? raw.value_utc : null,
    value_local: raw.value_local != null ? raw.value_local : null,
    timezone: raw.timezone != null ? raw.timezone : null,
    status: raw.status || 'Not Provided',
    days_to_expiry: typeof raw.days_to_expiry === 'number' ? raw.days_to_expiry : null,
    last_updated_at: raw.last_updated_at != null ? raw.last_updated_at : null,
    last_updated_by_role: raw.last_updated_by_role != null ? raw.last_updated_by_role : null,
    first_submitted_at: raw.first_submitted_at != null ? raw.first_submitted_at : null,
  }
}

function extractOpenRequest(raw) {
  if (!raw || !raw.request_id) return null
  return {
    request_id: raw.request_id,
    status: raw.status,
    awaiting_response_from: raw.awaiting_response_from,
    initial_raised_by_role: raw.initial_raised_by_role,
    initial_raised_by_name: raw.initial_raised_by_name,
    initial_raised_at: raw.initial_raised_at,
    current_round_number: raw.current_round_number,
    max_rounds: raw.max_rounds,
    rounds_remaining: raw.rounds_remaining,
    response_deadline_utc: raw.response_deadline_utc,
    is_past_deadline: !!raw.is_past_deadline,
    rounds: Array.isArray(raw.rounds) ? raw.rounds : [],
  }
}

function extractHistory(raw) {
  if (!Array.isArray(raw)) return []
  return raw.map((r) => ({
    request_id: r.request_id,
    status: r.status,
    terminal_action_by_role: r.terminal_action_by_role || null,
    terminal_action_by_name: r.terminal_action_by_name || null,
    terminal_action_at: r.terminal_action_at || null,
    initial_raised_at: r.initial_raised_at || null,
    rounds_count: r.rounds_count || null,
    final_resolved_validity_utc: r.final_resolved_validity_utc || null,
    final_resolved_round_number: r.final_resolved_round_number || null,
    rejection_reason: r.rejection_reason || null,
  }))
}

function errMsg(e, fallback) {
  return (
    (e && e.response && e.response.data && e.response.data.message)
    || (e && e.message)
    || fallback
  )
}

// ---- state -----------------------------------------------------------------

const state = () => ({
  // /v1/config/bid-validity — one-shot, cached
  config: null,
  configLoaded: false,
  configLoading: false,

  // /fetch/timezones — one-shot, cached
  // Items are { timeZone, label } to mirror RFQListing.vue's existing pattern
  timezones: [],
  timezonesLoaded: false,
  timezonesLoading: false,

  // Keyed by bidId
  validityByBidId: {},                  // bid_validity block
  openRequestByBidId: {},               // OpenRequest object or null
  historyByBidId: {},
  auditByBidId: {},

  // Quick flag from /v1/bid/header_details (independent of /pending fetch)
  hasPendingByBidId: {},                // bidId → boolean
  extensionRequestIdByBidId: {},        // bidId → string | null

  loadingByBidId: {},
  loadedByBidId: {},

  error: null,
})

// ---- getters ---------------------------------------------------------------

const getters = {
  config: (s) => s.config,
  timezones: (s) => s.timezones,

  validity: (s) => (bidId) => s.validityByBidId[bidId] || null,
  openRequest: (s) => (bidId) => s.openRequestByBidId[bidId] || null,
  history: (s) => (bidId) => s.historyByBidId[bidId] || [],
  audit: (s) => (bidId) => s.auditByBidId[bidId] || [],

  hasPendingExtensionRequest: (s) => (bidId) => !!s.hasPendingByBidId[bidId],
  extensionRequestId: (s) => (bidId) => s.extensionRequestIdByBidId[bidId] || null,

  isVendorTurn: (s) => (bidId) => {
    const r = s.openRequestByBidId[bidId]
    return !!r && r.awaiting_response_from === 'Vendor' && !r.is_past_deadline
  },

  isEpcTurn: (s) => (bidId) => {
    const r = s.openRequestByBidId[bidId]
    return !!r && r.awaiting_response_from === 'EPC' && !r.is_past_deadline
  },

  canCounter: (s) => (bidId) => {
    const r = s.openRequestByBidId[bidId]
    if (!r) return false
    if (r.awaiting_response_from !== 'EPC') return false
    if (r.is_past_deadline) return false
    return (r.rounds_remaining || 0) > 0
  },

  // V7c reference — EPC's counter must differ from the vendor's last proposal.
  latestVendorProposedUtc: (s) => (bidId) => {
    const r = s.openRequestByBidId[bidId]
    if (!r || !Array.isArray(r.rounds) || r.rounds.length === 0) return null
    for (let i = r.rounds.length - 1; i >= 0; i--) {
      if (r.rounds[i] && r.rounds[i].actor_role === 'Vendor') {
        return r.rounds[i].proposed_validity_utc || null
      }
    }
    return null
  },

  isExpiringSoon: (s) => (bidId) => {
    const v = s.validityByBidId[bidId]
    return !!v && v.status === 'Expiring Soon'
  },
  isExpired: (s) => (bidId) => {
    const v = s.validityByBidId[bidId]
    return !!v && v.status === 'Expired'
  },

  isLoading: (s) => (bidId) => !!s.loadingByBidId[bidId],
  isLoaded: (s) => (bidId) => !!s.loadedByBidId[bidId],
}

// ---- mutations -------------------------------------------------------------

const mutations = {
  SET_CONFIG(state, cfg) { state.config = cfg },
  SET_CONFIG_LOADING(state, v) { state.configLoading = v },
  SET_CONFIG_LOADED(state, v) { state.configLoaded = v },

  SET_TIMEZONES(state, list) { state.timezones = list },
  SET_TIMEZONES_LOADING(state, v) { state.timezonesLoading = v },
  SET_TIMEZONES_LOADED(state, v) { state.timezonesLoaded = v },

  SET_VALIDITY(state, { bidId, payload }) {
    state.validityByBidId = { ...state.validityByBidId, [bidId]: payload }
  },
  SET_OPEN_REQUEST(state, { bidId, payload }) {
    state.openRequestByBidId = { ...state.openRequestByBidId, [bidId]: payload }
  },
  SET_HISTORY(state, { bidId, payload }) {
    state.historyByBidId = { ...state.historyByBidId, [bidId]: payload }
  },
  SET_AUDIT(state, { bidId, payload }) {
    state.auditByBidId = { ...state.auditByBidId, [bidId]: payload }
  },
  SET_HAS_PENDING(state, { bidId, payload }) {
    state.hasPendingByBidId = { ...state.hasPendingByBidId, [bidId]: !!payload }
  },
  SET_EXTENSION_REQUEST_ID(state, { bidId, payload }) {
    state.extensionRequestIdByBidId = { ...state.extensionRequestIdByBidId, [bidId]: payload || null }
  },
  SET_LOADING(state, { bidId, value }) {
    state.loadingByBidId = { ...state.loadingByBidId, [bidId]: !!value }
  },
  SET_LOADED(state, { bidId, value }) {
    state.loadedByBidId = { ...state.loadedByBidId, [bidId]: !!value }
  },
  SET_ERROR(state, e) { state.error = e },

  // Clears all cache for a given bidId (used after mutations to force refetch).
  CLEAR_FOR_BID(state, bidId) {
    const drop = (m) => {
      const next = { ...m }
      delete next[bidId]
      return next
    }
    state.validityByBidId = drop(state.validityByBidId)
    state.openRequestByBidId = drop(state.openRequestByBidId)
    state.historyByBidId = drop(state.historyByBidId)
    state.auditByBidId = drop(state.auditByBidId)
    state.hasPendingByBidId = drop(state.hasPendingByBidId)
    state.extensionRequestIdByBidId = drop(state.extensionRequestIdByBidId)
    state.loadingByBidId = drop(state.loadingByBidId)
    state.loadedByBidId = drop(state.loadedByBidId)
  },
}

// ---- actions ---------------------------------------------------------------

const actions = {
  /**
   * One-shot, cached. GET ${VUE_APP_API_BID}/config/bid-validity.
   */
  async fetchConfig({ state, commit, dispatch }, opts) {
    const force = !!(opts && opts.force)
    if (!force && state.configLoaded) return state.config
    if (state.configLoading) return state.config
    commit('SET_CONFIG_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const res = await axios.get(`${process.env.VUE_APP_API_BID}/config/bid-validity`)
      const raw = (res && res.data && res.data.data) || (res && res.data) || {}
      const cfg = {
        min_validity_days_from_submission: raw.min_validity_days_from_submission != null ? raw.min_validity_days_from_submission : 7,
        max_validity_days_from_submission: raw.max_validity_days_from_submission != null ? raw.max_validity_days_from_submission : null,
        expiry_warning_days: raw.expiry_warning_days != null ? raw.expiry_warning_days : 7,
        epc_extension_request_response_deadline_days:
          raw.epc_extension_request_response_deadline_days != null ? raw.epc_extension_request_response_deadline_days : 5,
        max_negotiation_rounds: raw.max_negotiation_rounds != null ? raw.max_negotiation_rounds : 6,
        supported_timezones: Array.isArray(raw.supported_timezones) ? raw.supported_timezones : [],
      }
      commit('SET_CONFIG', cfg)
      commit('SET_CONFIG_LOADED', true)
      return cfg
    } catch (e) {
      const msg = errMsg(e, 'Failed to load bid validity config')
      commit('SET_ERROR', msg)
      dispatch('notification/add', { type: 'error', message: msg }, { root: true })
      throw e
    } finally {
      commit('SET_CONFIG_LOADING', false)
    }
  },

  /**
   * One-shot, cached. GET ${VUE_APP_REQ}/fetch/timezones.
   * Mirrors RFQListing.vue:1143-1145 → maps [tz, offset] tuples to
   * [{ timeZone, label }].
   */
  async fetchTimezones({ state, commit, dispatch }, opts) {
    const force = !!(opts && opts.force)
    if (!force && state.timezonesLoaded) return state.timezones
    if (state.timezonesLoading) return state.timezones
    commit('SET_TIMEZONES_LOADING', true)
    commit('SET_ERROR', null)
    try {
      const res = await axios.get(`${process.env.VUE_APP_REQ}/fetch/timezones`)
      const rawList = (res && res.data && res.data.data && res.data.data.timezones)
        || (res && res.data && res.data.timezones)
        || []
      const list = Array.isArray(rawList)
        ? rawList
            .filter((t) => Array.isArray(t) && t.length >= 1)
            .map((t) => ({
              timeZone: t[0],
              label: t[1] ? `${t[0]} ${t[1]}` : t[0],
            }))
        : []
      commit('SET_TIMEZONES', list)
      commit('SET_TIMEZONES_LOADED', true)
      return list
    } catch (e) {
      const msg = errMsg(e, 'Failed to load timezones')
      commit('SET_ERROR', msg)
      dispatch('notification/add', { type: 'error', message: msg }, { root: true })
      throw e
    } finally {
      commit('SET_TIMEZONES_LOADING', false)
    }
  },

  /**
   * SG2-US1 — GET ${VUE_APP_API_BID}/bid/header_details?bid_id=X.
   * Extracts the new SIBLING fields (bid_validity, has_pending_extension_request,
   * extension_request_id). `data` itself is the existing offer-header dict and
   * is left to whatever called this; we only commit the F5 siblings.
   *
   * Returns the full response body so callers can read `data` if they need it.
   */
  async fetchHeaderDetails({ commit, dispatch }, { bidId }) {
    if (!bidId) throw new Error('bidId required')
    commit('SET_ERROR', null)
    try {
      const res = await axios.get(`${process.env.VUE_APP_API_BID}/bid/header_details`, {
        params: { bid_id: bidId },
      })
      const body = (res && res.data) || {}
      commit('SET_VALIDITY', { bidId, payload: extractValidity(body.bid_validity) })
      commit('SET_HAS_PENDING', { bidId, payload: body.has_pending_extension_request })
      commit('SET_EXTENSION_REQUEST_ID', { bidId, payload: body.extension_request_id })
      return body
    } catch (e) {
      const msg = errMsg(e, 'Failed to load bid header details')
      commit('SET_ERROR', msg)
      dispatch('notification/add', { type: 'error', message: msg }, { root: true })
      throw e
    }
  },

  /**
   * Parallel GET pending + audit. Populates rounds[], history, audit, validity.
   * Used by the extension request banner which needs the full rounds[] history that
   * /header_details does not return.
   */
  async fetchForBid({ state, commit, dispatch }, { bidId, force }) {
    if (!bidId) return
    if (!force && state.loadedByBidId[bidId]) return
    if (!force && state.loadingByBidId[bidId]) return
    commit('SET_LOADING', { bidId, value: true })
    commit('SET_ERROR', null)
    try {
      const base = process.env.VUE_APP_API_BID
      const [pendingRes, auditRes] = await Promise.all([
        axios.get(`${base}/bid/validity/extension/pending`, { params: { bid_id: bidId } }),
        axios.get(`${base}/bid/validity/audit`, { params: { bid_id: bidId } }),
      ])
      const pendingBody = (pendingRes && pendingRes.data && pendingRes.data.data) || (pendingRes && pendingRes.data) || {}
      commit('SET_VALIDITY', { bidId, payload: extractValidity(pendingBody.current_validity) })
      commit('SET_OPEN_REQUEST', { bidId, payload: extractOpenRequest(pendingBody.open_request) })
      commit('SET_HISTORY', { bidId, payload: extractHistory(pendingBody.history) })

      const auditBody = (auditRes && auditRes.data && auditRes.data.data) || (auditRes && auditRes.data) || []
      commit('SET_AUDIT', { bidId, payload: Array.isArray(auditBody) ? auditBody : [] })

      commit('SET_LOADED', { bidId, value: true })
    } catch (e) {
      const msg = errMsg(e, 'Failed to load bid validity')
      commit('SET_ERROR', msg)
      dispatch('notification/add', { type: 'error', message: msg }, { root: true })
      throw e
    } finally {
      commit('SET_LOADING', { bidId, value: false })
    }
  },

  /** Convenience: force-refresh a bid. */
  async refresh({ dispatch }, bidId) {
    return dispatch('fetchForBid', { bidId, force: true })
  },

  // SG2-US3a — EPC raises a new request. POST /bid/validity/extension/request.
  // Phase 4 wires the UI; action exists here so unit tests can target it now.
  async raiseExtension({ commit, dispatch }, { bidId, payload }) {
    if (!bidId) throw new Error('bidId required')
    if (!payload || !payload.requested_validity_date) throw new Error('Incomplete extension payload')
    commit('SET_ERROR', null)
    try {
      const res = await axios.post(
        `${process.env.VUE_APP_API_BID}/bid/validity/extension/request`,
        {
          bid_id: bidId,
          requested_validity_date: payload.requested_validity_date,
          requested_validity_time_comp: payload.requested_validity_time_comp,
          requested_validity_timezone: payload.requested_validity_timezone,
          response_deadline_days: payload.response_deadline_days,
          message: payload.message,
        }
      )
      const data = (res && res.data && res.data.data) || (res && res.data) || {}
      // Refresh authoritative state so the UI updates without a manual reload.
      await dispatch('fetchHeaderDetails', { bidId })
      await dispatch('fetchForBid', { bidId, force: true })
      const successMsg = (res && res.data && res.data.message) || 'Extension request sent'
      dispatch('notification/add', { type: 'success', message: successMsg }, { root: true })
      return data
    } catch (e) {
      const msg = errMsg(e, 'Failed to raise extension request')
      commit('SET_ERROR', msg)
      dispatch('notification/add', { type: 'error', message: msg }, { root: true })
      throw e
    }
  },

  // SG2-US3b — EPC responds to a vendor counter. POST /bid/validity/extension/respond-epc.
  // NOTE: distinct from the vendor's /respond endpoint. Three actions:
  // accept | counter | reject. Phase 5 wires the UI; defined here for tests.
  async respondAsEpc({ commit, dispatch }, { bidId, requestId, action, counterPayload, reason, deadlineDays, message }) {
    if (!requestId) throw new Error('requestId required')
    if (!['accept', 'counter', 'reject'].includes(action)) {
      throw new Error('Invalid action — must be accept | counter | reject')
    }
    if (action === 'reject' && !String(reason || '').trim()) {
      throw new Error('Rejection reason is required')
    }
    commit('SET_ERROR', null)
    try {
      const body = { request_id: requestId, action }
      if (action === 'counter') {
        if (!counterPayload || !counterPayload.counter_validity_date) {
          throw new Error('Incomplete counter payload')
        }
        body.counter_validity_date = counterPayload.counter_validity_date
        body.counter_validity_time_comp = counterPayload.counter_validity_time_comp
        body.counter_validity_timezone = counterPayload.counter_validity_timezone
        if (typeof deadlineDays === 'number' && deadlineDays > 0) {
          body.response_deadline_days = deadlineDays
        }
        if (message) body.message = String(message)
      }
      if (action === 'reject') {
        body.rejection_reason = String(reason).trim()
      }

      const res = await axios.post(
        `${process.env.VUE_APP_API_BID}/bid/validity/extension/respond-epc`,
        body
      )
      const data = (res && res.data && res.data.data) || (res && res.data) || {}

      // Refresh state. bidId is optional when callers only have requestId;
      // but if we have it, refetch the bid-scoped views.
      if (bidId) {
        await dispatch('fetchForBid', { bidId, force: true })
        await dispatch('fetchHeaderDetails', { bidId })
      }
      const successMsg = (res && res.data && res.data.message) || ({
        accept: 'Counter accepted',
        counter: 'Counter sent',
        reject: 'Counter rejected',
      }[action])
      dispatch('notification/add', { type: 'success', message: successMsg }, { root: true })
      return data
    } catch (e) {
      const msg = errMsg(e, 'Failed to respond to extension request')
      commit('SET_ERROR', msg)
      dispatch('notification/add', { type: 'error', message: msg }, { root: true })
      throw e
    }
  },
}

// Exported helpers for unit tests
export { extractValidity, extractOpenRequest, extractHistory }

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
